import { css } from '@emotion/react';
import type { CSSInterpolation } from '@emotion/serialize';
import type {
	ComponentPropsWithoutRef,
	MouseEventHandler,
	ReactNode,
	Ref,
} from 'react';
import { forwardRef, useMemo } from 'react';

import { su, theme, typography } from '@adflow/frontend/ui-theme';
import { useForwardingRef } from '@adflow/frontend/util-common';

export const ERROR_TRANSITION_LENGTH = '0.3s';
export const ERROR_TRANSITION_FUNC = 'ease-out';

const styles = {
	input: css(typography.inputText, {
		minWidth: 0,
		flexGrow: 1,
		padding: '0.45rem 0',
		resize: 'vertical',
		outline: 0,
		'&::placeholder': css(typography.placeholder, {
			opacity: 1,
		}),
	}),
	inputContainerNumeric: css({
		'input::-webkit-outer-spin-button, input::-webkit-inner-spin-button': {
			WebkitAppearance: 'none',
			margin: 0,
		},
		"input[type='number']": {
			MozAppearance: 'textfield',
		},
	}),
	inputContainer: css({
		display: 'flex',
		alignItems: 'center',
		width: '100%',
		paddingLeft: '0.75rem',
		paddingRight: '0.75rem',
		paddingTop: '0px',
		paddingBottom: '0px',
		cursor: 'text',
		backgroundColor: theme.colors.background.paper,
		borderRadius: 2,
		border: '1px solid',
		borderColor: theme.colors.input.border,
		transition: `border-color ${ERROR_TRANSITION_LENGTH} ${ERROR_TRANSITION_FUNC}`,
		'&:focus-within': css(su.shadow, {
			borderColor: theme.colors.input.focusedBorder,
		}),
	}),
	inputContainerError: css({
		borderColor: theme.colors.palettes.error.main,
		'&:focus-within': {
			borderColor: theme.colors.palettes.error.main,
		},
	}),
	inputContainerDisabled: css({
		cursor: 'default',
		color: theme.colors.input.disabledText,
		backgroundColor: theme.colors.input.disabledBackground,
		borderColor: theme.colors.input.disabledBorder,
	}),
	inputContainerArchived: css({
		backgroundColor: theme.colors.background.paperTransparent,
	}),
	inputContainerReadOnly: css({
		backgroundColor: theme.colors.background.fullTransparent,
		cursor: 'default',

		borderColor: 'transparent',
		'&:focus-within': css({
			boxShadow: 'none',
			borderColor: 'transparent',
		}),
	}),

	inputArchived: css({
		backgroundColor: theme.colors.background.fullTransparent,
	}),
	inputReadOnly: css({
		backgroundColor: theme.colors.background.fullTransparent,
	}),
};

export const inputStyles = styles;

export interface InputStatus {
	error?: boolean;
	archived?: boolean;
	readOnly?: boolean;
	disabled?: boolean;
}

export interface InputProps
	extends InputStatus, ComponentPropsWithoutRef<'input'> {
	containerRef?: Ref<HTMLDivElement>;
	inputRef?: Ref<HTMLInputElement>;
	startAdornment?: ReactNode;
	endAdornment?: ReactNode;

	containerStyle?: CSSInterpolation;
	inputStyle?: CSSInterpolation;
	containerClassName?: string;

	forceAutoComplete?: boolean;
	disablePasswordManager?: boolean;
	autoCompleteOff?: boolean;

	/**
	 * Use a random name for the input to prevent browsers from showing
	 * autocomplete suggestions.
	 */
	useRandomName?: boolean;
	min?: string | number;
	max?: string | number;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
	function InputRender(props, forwardedRef) {
		const {
			error,
			archived,
			disabled,
			readOnly,
			containerRef,
			inputRef: inputRefProp,
			startAdornment,
			endAdornment,
			containerStyle,
			inputStyle,
			containerClassName,
			forceAutoComplete,
			disablePasswordManager = true,
			autoCompleteOff,
			autoComplete,
			useRandomName = true,
			...other
		} = props;

		const inputRef = useForwardingRef<HTMLInputElement | null>(null, [
			inputRefProp,
			forwardedRef,
		]);

		const onContainerClick: MouseEventHandler<HTMLDivElement> = (event) => {
			// Because the input container looks like part of the input it should
			// focus the input when the user clicks on the container.
			if (inputRef.current && event.currentTarget === event.target) {
				inputRef.current.focus();
			}
		};

		// We cannot use `useId` because it reuses the same values and Chrome will
		// remember those values and display autocomplete suggestions.
		//
		// `useMemo` is used to avoid extra DOM mutations.
		const randomName = useMemo(() => Math.random().toString(), []);

		return (
			<div
				ref={containerRef}
				css={[
					styles.inputContainer,
					other?.type === 'number' && styles.inputContainerNumeric,
					error && styles.inputContainerError,
					disabled && styles.inputContainerDisabled,
					archived && styles.inputContainerArchived,
					readOnly && styles.inputContainerReadOnly,
					containerStyle,
				]}
				className={containerClassName}
				onClick={(e) => {
					onContainerClick(e);
				}}
			>
				{startAdornment}
				<input
					ref={inputRef}
					css={[
						styles.input,
						disabled && styles.inputContainerDisabled,
						archived && styles.inputArchived,
						readOnly && { display: 'none !important' },
						inputStyle,
					]}
					disabled={disabled}
					readOnly={readOnly}
					{...other}
					name={useRandomName ? randomName : other.name}
					autoComplete={
						forceAutoComplete
							? 'on'
							: autoCompleteOff
								? 'off'
								: autoComplete || 'chrome-off'
					}
					// Disable Keeper password manager.
					className={
						disablePasswordManager
							? 'keeper-ignore ' + other.className
							: other.className
					}
					// Disable LastPass password manager.
					data-lpignore={disablePasswordManager}
					aria-invalid={error}
				/>

				{readOnly ? (
					<div
						css={[
							styles.input,
							disabled && styles.inputContainerDisabled,
							archived && styles.inputArchived,
							styles.inputReadOnly,
							inputStyle,
							{ resize: 'none', wordBreak: 'break-word' },
						]}
						{...other}
					>
						{other.value || <span>&nbsp;</span>}
					</div>
				) : null}
				{endAdornment}
			</div>
		);
	}
);
